/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AddressControlOptions } from '@epilot/journey-logic-commons'
import { FIELD_MAPPING } from '@epilot/journey-logic-commons'
import type { TFunction } from 'i18next'

import { mapToContactOptions } from '../ContactControl'

import type {
  AddressControlOptionsDto,
  AddressControlOptionsLegacy
} from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isLegacyDto = (obj: any): obj is AddressControlOptionsLegacy =>
  obj && obj['fieldsOrder']

const isFieldInMapping = (key: any): key is keyof typeof FIELD_MAPPING => {
  if (typeof key === 'string') {
    if (key in FIELD_MAPPING) return true

    return false
  }

  return false
}

export const mapToAddressOptions = (
  dto?: AddressControlOptionsDto,
  translate?: TFunction,
  disabledFields?: string[]
): AddressControlOptions | { error: string } => {
  const fields: AddressControlOptions['fields'] = {}
  const unlistedFields: AddressControlOptions['unlistedFields'] = {
    zipCity: {}
  }
  const t = translate || (((value: string) => value) as TFunction)

  if (!dto) return { error: 'Incoming DTO is not set' }

  // the following function is intercepting the flow to map the legacy dto to the new dto
  // it will also mark field as disabled if it is part of the disabledFields array
  if (!isLegacyDto(dto)) {
    if (disabledFields) {
      const { fields } = dto

      ;(Object.keys(fields) as Array<keyof typeof fields>).forEach(
        (fieldName) => {
          Object.assign(fields, {
            [fieldName]: {
              ...fields[fieldName],
              ...(disabledFields?.includes(fieldName) ? { disabled: true } : {})
            }
          })
        }
      )

      return { ...dto, fields }
    }

    if (!dto.unlistedFields) {
      return {
        ...dto,
        unlistedFields
      }
    } else if (!('zipCity' in dto.unlistedFields)) {
      return {
        ...dto,
        unlistedFields: {
          ...dto.unlistedFields,
          zipCity: {}
        }
      }
    } else {
      return dto
    }
  }

  const fieldsDto =
    dto.fieldsOrder && Array.isArray(dto.fieldsOrder) ? dto.fieldsOrder : []
  const fieldsProcessed: string[] = []

  try {
    fieldsDto.forEach((item: any) => {
      const fieldName = item.name

      if (!fieldName) throw 'DTO Mapping -> field name does not exist'

      // do not include unsused fields
      if (!isFieldInMapping(fieldName)) return

      let placeholder: string | undefined = undefined
      const key = FIELD_MAPPING[fieldName]['name']
      const keyLabel = FIELD_MAPPING[fieldName]['label']
      const keyPlaceholder = FIELD_MAPPING[fieldName]['placeholder']

      const label = dto.labels?.[keyLabel]
      const required =
        dto.requiredFields?.[key] || // if key is streetName, check also for key street
        (key === 'streetName' ? dto.requiredFields?.['street'] : undefined)
      const disabled =
        dto.disabledFields?.[key] || // if key is streetName, check also for key street
        (key === 'streetName' ? dto.disabledFields?.['street'] : undefined)

      const layout = item.gridItemProps

      placeholder = dto.placeholders
        ? dto.placeholders[keyPlaceholder]
        : undefined

      Object.assign(fields, {
        [FIELD_MAPPING[fieldName]['targetName']]: {
          ...(label && { label }),
          ...(placeholder && { placeholder }),
          ...(required && { required }),
          ...(disabled && { disabled }),
          ...(layout && { layout }),
          ...(disabledFields?.includes(fieldName) ? { disabled: true } : {})
        }
      })

      // add to processed fields
      fieldsProcessed.push(fieldName)
    })

    // map contact related fields. If error, return, else merge contact fields
    // todo: fix the type NEVER here
    const data = mapToContactOptions(dto as never, t, fieldsProcessed)

    if ('error' in data) {
      return {
        error: data.error
      }
    }

    Object.assign(fields, { ...data.fields })
  } catch (error) {
    // if any error in mapping, return it here
    if (error instanceof Error) {
      return { error: error.message }
    } else {
      return { error: `${error}` }
    }
  }

  return {
    fields,
    unlistedFields,
    acceptSuggestedOnly: dto.acceptOnlySuggestedAddresses,
    countryAddressSettings: (dto as any).countryAddressSettings
  }
}
